import { useEffect, useLayoutEffect, useRef, useState } from "react"

const useOverflowDetection = (debounceMs = 100) => {
	const contentRef = useRef<HTMLDivElement>(null)
	const [isOverflowing, setIsOverflowing] = useState(false)
	const resizeObserverRef = useRef<ResizeObserver | null>(null)
	const timeoutRef = useRef<NodeJS.Timeout>()

	// Immediate overflow check after DOM updates
	useLayoutEffect(() => {
		if (contentRef.current) {
			const element = contentRef.current
			setIsOverflowing(element.scrollWidth > element.clientWidth)
		}
	}, [])

	useEffect(() => {
		if (!contentRef.current) return

		const checkOverflow = () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
			}

			timeoutRef.current = setTimeout(() => {
				if (contentRef.current) {
					const element = contentRef.current
					setIsOverflowing(element.scrollWidth > element.clientWidth)
				}
			}, debounceMs)
		}

		// Initial check with a small delay to ensure DOM is ready
		const initialCheck = () => {
			if (contentRef.current) {
				const element = contentRef.current
				setIsOverflowing(element.scrollWidth > element.clientWidth)
			}
		}

		// Run initial check after a brief delay to ensure rendering is complete
		const initialTimeout = setTimeout(initialCheck, 50)

		resizeObserverRef.current = new ResizeObserver(checkOverflow)
		resizeObserverRef.current.observe(contentRef.current)

		checkOverflow()

		return () => {
			clearTimeout(initialTimeout)
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
			}
			if (resizeObserverRef.current) {
				resizeObserverRef.current.disconnect()
			}
		}
	}, [debounceMs])

	return { contentRef, isOverflowing }
}

export default useOverflowDetection
